import type { Project } from '~/types/project.type'
import type { Task } from '~/types/task.type'
import type { TaskList } from '~/types/taskList.type'
import { TaskDeleteModal, TaskModal } from '#components'
import { unknownError } from '~/constants'

export default function () {
  const overlay = useOverlay()
  const toast = useToast()
  const loadingBar = useNaiveLoadingBar()
  const taskListsFilter = ref<ApiTaskListGetListParams>({})

  const { data: taskListsData, execute: fetchTaskLists } = useApiTaskListGetList(taskListsFilter, {
    immediate: false,
    watch: false,
  })

  const { data: projectsData, execute: fetchProjects } = useApiProjectGetListUnpaginated({
    isActive: true,
    withLeads: true,
    withClient: true,
  }, {
    immediate: false,
    watch: false,
  })

  const isTaskCreateModalLoading = ref(false)

  const taskEditModal = overlay.create(TaskModal, { props: { title: 'Aufgabe bearbeiten' } })

  async function openTaskCreateModal(options: {
    taskListsFilterToApply: ApiTaskListGetListParams
    taskListId?: TaskList['id']
    projectId?: Project['id']
    taskName?: string
  } = {
    taskListsFilterToApply: {
      withCompleted: false,
    },
  }, onTaskCreated?: (task: Task<undefined>) => any) {
    loadingBar.start()
    isTaskCreateModalLoading.value = true
    const promises = [fetchProjects()]
    if (options.projectId) {
      taskListsFilter.value = {
        ...options.taskListsFilterToApply,
        projectId: options.projectId,
      }
      promises.push(fetchTaskLists())
    }
    await Promise.all(promises)
    loadingBar.finish()
    isTaskCreateModalLoading.value = false

    const taskCreateModal = overlay.create(TaskModal, { props: { title: 'Aufgabe erstellen' } })

    return taskCreateModal.open({
      projects: projectsData.value?.data ?? undefined,
      projectId: options.projectId,
      taskListId: options.taskListId,
      taskLists: options.projectId ? taskListsData.value?.data : undefined,
      taskListFilterToApply: taskListsFilter.value,
      taskName: options.taskName,
      onTaskCreated,
    })
  }

  const taskToEditId = ref<Task['id']>(0)

  const {
    data: taskToEditData,
    execute: fetchTaskToEdit,
    error: taskEditError,
  } = useApiTaskGetById(taskToEditId, {
    withTaskList: true,
    withNotificationThresholds: true,
    withProject: true,
  }, {
    immediate: false,
    watch: false,
  })

  const isAnyTaskEditModalLoading = ref(false)

  function isTaskEditModalLoading(taskId: Task['id']) {
    return computed(() => isAnyTaskEditModalLoading.value && taskToEditId.value === taskId)
  }

  async function openTaskEditModal(
    task: Task<undefined>,
    options: {
      taskListsFilterToApply: ApiTaskListGetListParams
      projectId?: Project['id']
    } = { taskListsFilterToApply: {} },
    onTaskEdited?: (task: Task<undefined>) => any,
    onTaskDeleted?: () => any,
  ) {
    if (isAnyTaskEditModalLoading.value)
      return

    loadingBar.start()
    taskToEditId.value = task.id
    isAnyTaskEditModalLoading.value = true
    await fetchTaskToEdit()
    taskListsFilter.value = {
      ...options.taskListsFilterToApply,
      projectId: taskToEditData.value?.data.taskList.projectId || options.projectId,
    }
    await Promise.all([fetchTaskLists(), fetchProjects()])
    loadingBar.finish()
    isAnyTaskEditModalLoading.value = false

    if (!taskEditError.value) {
      return taskEditModal.open({
        title: `Aufgabe bearbeiten: ${task.name}`,
        task: taskToEditData.value?.data ?? undefined,
        taskListFilterToApply: taskListsFilter.value,
        projectId: options.projectId,
        projects: projectsData.value?.data ?? undefined,
        taskLists: taskListsData.value?.data ?? undefined,
        onTaskEdited,
        onTaskDeleted,
      })
    }
    else {
      toast.add({
        color: 'error',
        description: taskEditError.value.data?.message ?? unknownError,
      })
    }
  }

  const taskToDeleteId = ref<Task['id']>(0)
  const { error: taskDeleteError, execute: executeDelete } = useApiTaskDeleteById(taskToDeleteId)

  const taskDeleteModal = overlay.create(TaskDeleteModal)

  async function openTaskDeleteDialog(task: Task<undefined>, onTaskDeleted?: () => any) {
    taskDeleteModal.open({
      onClose: (confirm: boolean) => confirm ? deleteTask(task, onTaskDeleted) : null,
      task,
    })
  }

  async function deleteTask(task: Task<undefined>, onTaskDeleted?: () => any) {
    loadingBar.start()
    taskToDeleteId.value = task.id
    await executeDelete()
    loadingBar.finish()

    if (!taskDeleteError.value) {
      toast.add({
        color: 'success',
        description: 'Aufgabe erfolgreich gelöscht!',
      })

      if (onTaskDeleted)
        onTaskDeleted()
    }
    else {
      toast.add({
        color: 'error',
        description: taskDeleteError.value.data?.message ?? unknownError,
      })
    }
  }

  return {
    isTaskCreateModalLoading,
    openTaskCreateModal,
    isAnyTaskEditModalLoading,
    openTaskEditModal,
    isTaskEditModalLoading,
    openTaskDeleteDialog,
  }
}
